/**
 * Consistent HE / Subcontractor PDF download names:
 * `<Type>_<Vendor>_<Date>_<ID>.pdf`
 */

type HePdfFilenameField = {
  field: string;
  path?: string;
  value?: string | null;
};

function sanitizePdfFilenamePart(value: string, fallback: string): string {
  const cleaned = value
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return cleaned || fallback;
}

function extractHePdfTypeLabel(fields: HePdfFilenameField[]): string {
  const raw = fields.find(
    (field) => field.field === 'SubcontractorOrHERep' || (field.path || '').includes('SubcontractorOrHERep'),
  )?.value;
  const trimmed = String(raw || '').trim();
  if (/subcontractor/i.test(trimmed)) return 'Subcontractor';
  return 'Hired_Equipment';
}

function extractHePdfVendorName(fields: HePdfFilenameField[]): string {
  const setup = fields
    .find((field) => field.field === 'SubcontractorNameCom' && !(field.path || '').includes('WorkCompleted'))
    ?.value?.trim();
  if (setup) return setup;
  return fields.find((field) => field.field === 'SubcontractorNameCom')?.value?.trim() || '';
}

function extractHePdfDateYmd(fields: HePdfFilenameField[]): string {
  const actualStart = fields.find((field) => field.field === 'ActualStartTime' && field.value?.trim())?.value?.trim();
  if (actualStart) {
    const isoDay = actualStart.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDay) return isoDay[1];
    const parsed = new Date(actualStart);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
  return new Date().toISOString().slice(0, 10);
}

export function buildHiredEquipmentPdfFilename(form: {
  id: string | number;
  fields: HePdfFilenameField[];
}): string {
  const type = extractHePdfTypeLabel(form.fields);
  const vendor = sanitizePdfFilenamePart(extractHePdfVendorName(form.fields), 'Vendor');
  const date = extractHePdfDateYmd(form.fields);
  return `${type}_${vendor}_${date}_${String(form.id)}.pdf`;
}
